<template>
  <div
    class="
      flex-1 flex flex-col
      bg-white-005
      px-3
      md:px-6
      py-3
      md:py-4
      rounded-2xl
      mb-6
      md:mb-0
    "
    style="height: 392px; max-width: 588px"
  >
    <div class="text-sm lg:text-xl font-bold mb-6">
      Weekly DAO Treasury Inflow/Outflow By Valuation
    </div>
    <div
      style="max-width: 540px"
      class="flex-1 min-h-192 h-full"
      ref="container"
    >
      <VChart
        ref="chart"
        v-if="!loading"
        class="w-full min-h-192 h-full"
        :option="option"
        :key="width"
      ></VChart>
      <div v-else class="w-full h-full flex justify-center items-center">
        <Spin></Spin>
      </div>
    </div>
  </div>
</template>

<script setup>
import { BarChart } from 'echarts/charts';
import {
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

import { queryInOutChart } from '@/api/dao';
import Spin from '@/components/Spin.vue';
import { chartColor } from '@/constants';
import { useReqByBool } from '@/hooks';
import { formatVal } from '@/utils';
import dayjs from 'dayjs';
import { ref, computed, inject, onMounted, reactive } from 'vue';
import VChart from 'vue-echarts';
import { homeChains } from '../constant';
import { useElementSize } from '@vueuse/core';

const chart = ref(null);

const container = ref(null);
const { width, height } = useElementSize(container);
use([
  CanvasRenderer,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  GraphicComponent,
]);

const homeState = inject('homeState');

const state = reactive({
  list: [],
});

const data = computed(() => {
  const list = JSON.parse(JSON.stringify(state.list));
  list.reverse();
  return list;
});

const option = computed(() => {
  return {
    tooltip: {
      borderColor: '#FFFFFF66',
      backgroundColor: '#1e1f21bb',
      textStyle: {
        color: '#fff',
        fontSize: 12,
      },
      formatter({ dataIndex }) {
        const info = data.value[dataIndex];
        return `
        <div class="space-y-1">
            <div>${info.monday}</div>
            <div class="flex items-center">
              <div class="w-3 h-3 rounded mr-8" style="background: #9317b5ff"></div>
              Outflow: $${formatVal(info.outFlowUsd, { omit: true })}
            </div>
            <div class="flex items-center">
              <div class="w-3 h-3 rounded mr-8" style="background: #f72585ff"></div>
              Inflow: $${formatVal(info.inFlowUsd, { omit: true })}</div>
          </div>
        `;
      },
    },
    grid: {
      tope: 10,
      left: 60,
      right: 40,
      bottom: 20,
    },
    legend: {
      data: ['Inflow', 'Outflow'],
      textStyle: {
        color: '#fff',
      },
      left: 'left',
    },
    xAxis: {
      type: 'category',
      data: data.value.map((x) => dayjs(x.monday).format('M.D')),
    },
    yAxis: {
      type: 'value',
      splitLine: {
        lineStyle: {
          color: '#FFFFFF1A',
        },
      },
      axisLabel: {
        formatter: (val) => formatVal(val, { omit: true, bit: 2 }),
      },
    },
    color: chartColor,
    series: [
      {
        data: data.value.map((x) => x.inFlowUsd),
        type: 'bar',
        name: 'Inflow',
      },
      {
        data: data.value.map((x) => x.outFlowUsd),
        type: 'bar',
        name: 'Outflow',
      },
    ],
  };
});

const { loadData, loading } = useReqByBool(async () => {
  const res = await queryInOutChart(
    homeChains[homeState.selectedChain].value,
    homeState.filterStatus,
  );

  state.list = res;
});

onMounted(loadData);
</script>

<style lang="scss" scoped></style>
